import { useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import { Timepicker } from "timepicker-ui-react";
import type { ConfirmEventData } from "timepicker-ui";
import { ClockCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useAppTheme } from "../theme/ThemeProvider";
import "timepicker-ui/main.css";
import "timepicker-ui/theme-m3-green.css";
import "timepicker-ui/theme-dark.css";

export interface TimeValue {
  hour: string;
  minute: string;
}

interface TimePickerMobileProps {
  value?: TimeValue;
  disabled?: boolean;
  onChange: (value: TimeValue) => void;
  /** Nastaví aktuální datum a čas (volá rodič pro datetime otázky). */
  onPickNow?: () => void;
}

const EMPTY_TIME: TimeValue = { hour: "00", minute: "00" };

function formatTimeValue(value?: TimeValue): string {
  if (!value) return "";
  const hour = String(value.hour).padStart(2, "0");
  const minute = String(value.minute).padStart(2, "0");
  if (hour === "00" && minute === "00") return "";
  return `${hour}:${minute}`;
}

function parseConfirmData(data: ConfirmEventData): TimeValue {
  return {
    hour: String(data.hour ?? "00").padStart(2, "0"),
    minute: String(data.minutes ?? "00").padStart(2, "0"),
  };
}

export default function TimePickerMobile({
  value,
  disabled,
  onChange,
  onPickNow,
}: TimePickerMobileProps) {
  const { themeName } = useAppTheme();
  const [displayValue, setDisplayValue] = useState(() => formatTimeValue(value));

  useEffect(() => {
    setDisplayValue(formatTimeValue(value));
  }, [value?.hour, value?.minute]);

  const pickerOptions = useMemo(
    () => ({
      clock: { type: "24h" as const, autoSwitchToMinutes: true },
      ui: {
        theme: (themeName === "dark" ? "dark" : "m3-green") as "dark" | "m3-green",
        mode: "clock" as const,
        clearButton: true,
      },
      labels: {
        ok: "OK",
        cancel: "Zrušit",
        time: "Vyberte čas",
        mobileTime: "Vyberte čas",
        clear: "Vymazat",
      },
    }),
    [themeName]
  );

  const isDefaultTime = displayValue === "";

  const resetTime = () => {
    setDisplayValue("");
    onChange(EMPTY_TIME);
  };

  const applyNow = () => {
    if (disabled) return;
    if (onPickNow) {
      onPickNow();
      return;
    }
    const now = new Date();
    const next: TimeValue = {
      hour: String(now.getHours()).padStart(2, "0"),
      minute: String(now.getMinutes()).padStart(2, "0"),
    };
    setDisplayValue(formatTimeValue(next));
    onChange(next);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: 50,
    fontSize: 14,
    padding: "0 72px 0 12px",
    border: themeName === "dark" ? "1px solid #21262d" : "1px solid #d9d9d9",
    borderRadius: 6,
    cursor: disabled ? "not-allowed" : "pointer",
    backgroundColor: disabled
      ? themeName === "dark"
        ? "#161b22"
        : "#f5f5f5"
      : themeName === "dark"
        ? "#0d1117"
        : "#fff",
    color:
      themeName === "dark"
        ? isDefaultTime
          ? "#8b949e"
          : "#3ee7ed"
        : isDefaultTime
          ? "silver"
          : "black",
    opacity: disabled ? 0.7 : 1,
  };

  return (
    <div className="spotlight-timepicker-field">
      <div style={{ position: "relative" }}>
        <Timepicker
          value={displayValue}
          disabled={disabled}
          placeholder="Vyberte čas"
          options={pickerOptions}
          onConfirm={(data) => {
            const next = parseConfirmData(data);
            const formatted = formatTimeValue(next);
            setDisplayValue(formatted);
            onChange(next);
          }}
          onClear={resetTime}
          className="spotlight-timepicker-input"
          style={inputStyle}
        />

        <div
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            gap: 8,
            alignItems: "center",
            pointerEvents: disabled ? "none" : "auto",
          }}
        >
          {!isDefaultTime && (
            <CloseCircleOutlined
              style={{
                fontSize: 18,
                color: themeName === "dark" ? "#8b949e" : "silver",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) resetTime();
              }}
            />
          )}
          <ClockCircleOutlined
            style={{ fontSize: 18, color: themeName === "dark" ? "#8b949e" : "silver" }}
          />
        </div>
      </div>

      <Button
        type="dashed"
        block
        disabled={disabled}
        onClick={applyNow}
        style={{ marginTop: 8, height: 50, fontSize: 16 }}
      >
        Aktuální čas
      </Button>
    </div>
  );
}
